import { FC, useRef, useState } from 'react';
import { Reactions } from './Reactions';
import { Text } from '../../components/Text';
import { ICaremapNote } from '../../notes/model/NotesClient';
import { createUseStyles } from 'react-jss';
import { IPostInteractionInfoType, PostInteractionModal } from '../PostInteractionModal';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CaremapNotesModelFactory } from '../../notes/model/CaremapNotesModelFactory';
import { CommentIcon, PaperPlaneIcon } from '../../theme/Icons';
import { useLongPress } from '@uidotdev/usehooks';
import { isPlatform } from '@ionic/react';
import { CommentsModelFactory } from '../../comments/CommentsModelFactory';
import { useSubscription } from '@meraki-internal/state';
import { AddCommentButton } from '../AddCommentButton';
import { useBreakpoints } from '../../theme/useBreakpoints';
import { ViewCommunityPostWithQueryParamDrawer } from '../ViewCommunityPostWithQueryParamDrawer';

const useStyles = createUseStyles({
    interactionsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between'
    },

    supporterInfoRow: {
        gap: 16,
        flexGrow: 1,
        marginBottom: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },

    smallText: {
        cursor: 'pointer',
        marginTop:4,
        fontSize: 14
    },

    reactionsRow: {
        marginBottom: 8
    },

    cntIndicator: {
        display: 'flex',
        alignItems: 'center'
    }
});

export type IReactionsRowProps = {
    post: ICaremapNote;
    addCommentHandler?: (post: ICaremapNote) => void;
    commentCntClickHandler?: (post: ICaremapNote) => void;
};

export const ReactionsRow: FC<IReactionsRowProps> = ({ post, addCommentHandler, commentCntClickHandler }) => {
    const classes = useStyles();

    const viewPostDrawer = useInstance(ViewCommunityPostWithQueryParamDrawer);
    const reactionsModel = useInstance(CaremapNotesModelFactory).forActiveCaremap().reactions;

    const factory = useInstance(CommentsModelFactory);
    useSubscription(() => factory);
    const commentsModel = factory.forNote(post);

    const [modalInfo, setModalInfo] = useState<{ post: ICaremapNote, type: IPostInteractionInfoType } | undefined>();

    // using state didn't work, my guess is due to how the long press hook is set up, so using a ref works
    const modalType = useRef<{ type: IPostInteractionInfoType }>({type: 'reactions'});

    const { isAboveBreakpoint } = useBreakpoints();
    const isAboveSm = isAboveBreakpoint('sm');

    const showInfoModal = (type: IPostInteractionInfoType) => {
        setModalInfo({ type: modalType.current.type, post: post });
    };

    const isDesktop = isPlatform('desktop');
    const onLongPress = (e: Event) => {
        // on desktop, it's just a 'click' for Sent to show the modal and no events to show the modal on reactions
        if (!isDesktop) {
            showInfoModal(modalType.current?.type);
        }
    };

    const longPressAttrs = useLongPress(e => onLongPress(e), {
        threshold: 500,
        onStart: (e: any) => {
            if (!isDesktop) {
                modalType.current.type = 'reactions';
            }
        }
    });

    const showNotified = () => {
        modalType.current.type = 'notifications';
        showInfoModal(modalType.current?.type);
    };

    const getCommentCount = () => {
        const cnt = commentsModel.getComments().length;
        return cnt ?
            <div className={classes.cntIndicator}>
                <CommentIcon color="mediumtext" />&nbsp;{cnt}{isAboveSm ? ` Comment${cnt > 1 ? 's' : ''}` : ''}
            </div> : '';
    };

    const getNotificationCount = () => {
        const cnt = reactionsModel.getNames({note: post, reaction: 'notification-sent:email'}).length;
        return cnt ?
            <div className={classes.cntIndicator}>
                <PaperPlaneIcon color="mediumtext" />&nbsp;{cnt}{isAboveSm && ' Notified'}
            </div> : '';
    };

    const commentCnt = getCommentCount();
    const notificationCnt = getNotificationCount();

    return (
        <>
            <div
                {...longPressAttrs}
                className={classes.interactionsRow}
            >
                <div className={classes.reactionsRow}>
                    <Reactions post={post} />
                </div>
                <div className={classes.supporterInfoRow}>
                    <div style={{flex: 1}}>&nbsp;</div>
                    <div>
                        {addCommentHandler &&
                            <AddCommentButton
                                onClick={() => addCommentHandler(post)}
                                authenticatedRedirect={viewPostDrawer.getUrl({ postId: post.noteId, addComment: true })}
                            />
                        }
                    </div>
                    {commentCnt && commentCntClickHandler && (
                        <div className={classes.smallText} onClick={() => commentCntClickHandler(post)}>
                            <Text type="detail" data-type="post-comment-count">
                                {commentCnt}
                            </Text>
                        </div>
                    )}
                    {notificationCnt && (
                        <div className={classes.smallText} onClick={showNotified}>
                            <Text type="detail" data-type="post-notification-count">
                                {notificationCnt}
                            </Text>
                        </div>
                    )}
                </div>
            </div>

            <PostInteractionModal
                modalInfo={modalInfo}
                closeHandler={() => setModalInfo(undefined)}
            />
        </>
    );
};
